/**
 * Every non-2xx response from the backend is normalized into an `ApiError`.
 * Consumers narrow via `instanceof` and `status`, never by pattern-matching
 * on message strings.
 */

export type ApiErrorBody = { detail?: string; code?: string; [key: string]: unknown }

export class ApiError extends Error {
  readonly status: number
  readonly code?: string | undefined
  readonly body?: ApiErrorBody | undefined
  /** Seconds from Retry-After header on 429 responses; null otherwise. */
  readonly retryAfterSeconds: number | null

  constructor(
    status: number,
    message: string,
    opts?: { code?: string; body?: ApiErrorBody; retryAfterSeconds?: number | null },
  ) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = opts?.code
    this.body = opts?.body
    this.retryAfterSeconds = opts?.retryAfterSeconds ?? null
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isNotFound(): boolean {
    return this.status === 404
  }
  get isConflict(): boolean {
    return this.status === 409
  }
  get isRateLimited(): boolean {
    return this.status === 429
  }
  get isServer(): boolean {
    return this.status >= 500
  }
}

export class AbortedError extends Error {
  constructor(message = "Request aborted") {
    super(message)
    this.name = "AbortedError"
  }
}
